import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

const BookingContext = createContext();

// Mock data for services
const initialServices = [
  {
    id: '1',
    name: 'Classic Haircut',
    description: 'Traditional haircut with precision scissors and clippers',
    price: 35,
    duration: 30,
    image: '/assets/images/service-classic-haircut.jpg'
  },
  {
    id: '2',
    name: 'Beard Trim',
    description: 'Shape and trim your beard to perfection',
    price: 20,
    duration: 20,
    image: '/assets/images/service-beard-trim.jpg'
  },
  {
    id: '3',
    name: 'Haircut + Beard',
    description: 'Complete grooming package with haircut and beard styling',
    price: 50,
    duration: 45,
    image: '/assets/images/service-haircut-beard.jpg'
  },
  {
    id: '4',
    name: 'Hot Towel Shave',
    description: 'Luxurious traditional straight razor shave with hot towels',
    price: 40,
    duration: 35,
    image: '/assets/images/service-hot-towel.jpg'
  },
  {
    id: '5',
    name: 'Kids Haircut',
    description: 'Gentle haircut service for children under 12',
    price: 25,
    duration: 25,
    image: '/assets/images/service-kids.jpg'
  },
  {
    id: '6',
    name: 'Premium Package',
    description: 'Haircut, beard, hot towel, and scalp massage',
    price: 75,
    duration: 60,
    image: '/assets/images/service-premium.jpg'
  }
];

// Mock data for barbers/chairs
const initialBarbers = [
  {
    id: '1',
    name: 'Marcus Johnson',
    title: 'Master Barber',
    experience: '12 years',
    specialty: 'Classic cuts, Fades',
    image: '/assets/images/barber-marcus.jpg',
    rating: 4.9,
    reviews: 234
  },
  {
    id: '2',
    name: 'David Chen',
    title: 'Senior Barber',
    experience: '8 years',
    specialty: 'Modern styles, Beard design',
    image: '/assets/images/barber-david.jpg',
    rating: 4.8,
    reviews: 189
  },
  {
    id: '3',
    name: 'James Williams',
    title: 'Style Specialist',
    experience: '6 years',
    specialty: 'Creative designs, Kids cuts',
    image: '/assets/images/barber-james.jpg',
    rating: 4.7,
    reviews: 156
  },
  {
    id: '4',
    name: 'Any Available',
    title: 'Next Available Barber',
    experience: '',
    specialty: 'Fastest booking option',
    image: '/assets/images/barber-any.jpg',
    rating: null,
    reviews: null
  }
];

const ANY_AVAILABLE_BARBER_ID = initialBarbers[initialBarbers.length - 1].id;
const SLOT_INTERVAL_MINUTES = 30;
const DEFAULT_BOOKING_WINDOW = {
  enabled: true,
  firstSlotTime: '09:00',
  lastSlotTime: '18:00',
  closingTime: '18:00',
  walkInsWelcome: false
};
const DEFAULT_BOOKING_WEEKLY_RULES = Array.from({ length: 7 }, (_, weekday) => ({
  weekday,
  ...DEFAULT_BOOKING_WINDOW
}));
const WEEKDAY_INDEX_BY_SHORT_NAME = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6
};

const parseChairNumber = (barber) => {
  const value = String(barber?.name || '');
  const match = value.match(/(\d+)/);
  if (!match) return null;
  const parsed = Number(match[1]);
  if (!Number.isFinite(parsed) || parsed < 1) return null;
  return parsed;
};

const sortBarbersByChairNumber = (list) => {
  return [...(list || [])].sort((a, b) => {
    const aNum = parseChairNumber(a);
    const bNum = parseChairNumber(b);
    if (aNum != null && bNum != null && aNum !== bNum) return aNum - bNum;
    if (aNum != null && bNum == null) return -1;
    if (aNum == null && bNum != null) return 1;
    return String(a?.name || '').localeCompare(String(b?.name || ''));
  });
};

// Default to Ottawa/Toronto time when not configured.
const SHOP_TIMEZONE = process.env.REACT_APP_SHOP_TIMEZONE || 'America/Toronto';
const WHATSAPP_CONFIRMATION_ENABLED = process.env.REACT_APP_ENABLE_WHATSAPP_CONFIRMATION !== 'false';
const BACKGROUND_SYNC_INTERVAL_MS = 5000;
const METRICS_SYNC_INTERVAL_MS = 3000;
const BACKGROUND_SYNC_DEBOUNCE_MS = 500;
const DEFAULT_PROMO_IMAGE_ALT = "Shop promotion";

const normalizePromoCode = (value) => String(value || '').trim().toUpperCase();

const toMoney = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Number(Math.max(0, numeric).toFixed(2));
};

const computePromoDiscountAmount = (promoType, promoDiscount, baseAmount) => {
  const amount = toMoney(baseAmount);
  const discountValue = Number(promoDiscount);
  if (!Number.isFinite(discountValue) || discountValue <= 0 || amount <= 0) return 0;

  if (String(promoType || '').toLowerCase() === 'fixed') {
    return toMoney(Math.min(discountValue, amount));
  }

  return toMoney(Math.min((amount * discountValue) / 100, amount));
};

const sanitizePhoneDigits = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D+/g, '');
  return digits.startsWith('00') ? digits.slice(2) : digits;
};

const getDateTimePartsInTimeZone = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(date);

  const lookup = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    hour: Number(lookup.hour),
    minute: Number(lookup.minute)
  };
};

const formatDateLocal = (date, timeZone = SHOP_TIMEZONE) => {
  if (!(date instanceof Date) || Number.isNaN(date.valueOf())) return null;
  // Calendar-day extraction should be done in the shop timezone.
  // Callers should pass a date that already represents the intended shop day.
  const { year, month, day } = getDateTimePartsInTimeZone(date, timeZone);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const formatLocalCalendarDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.valueOf())) return null;
  return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const parseTimeToMinutes = (value) => {
  if (!/^\d{2}:\d{2}$/.test(String(value || ''))) return null;
  const [hourText, minuteText] = String(value).split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
};

const formatMinutesToTime = (minutes) => {
  if (!Number.isInteger(minutes) || minutes < 0) return null;
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const normalizeWeeklyRules = (rules) => {
  return DEFAULT_BOOKING_WEEKLY_RULES.map((fallbackRule) => {
    const match = (rules || []).find((rule) => Number(rule?.weekday) === fallbackRule.weekday);
    return {
      weekday: fallbackRule.weekday,
      enabled: match?.enabled ?? fallbackRule.enabled,
      firstSlotTime: String(match?.firstSlotTime || fallbackRule.firstSlotTime),
      lastSlotTime: String(match?.lastSlotTime || fallbackRule.lastSlotTime),
      closingTime: String(match?.closingTime || match?.lastSlotTime || fallbackRule.closingTime),
      walkInsWelcome: Boolean(match?.walkInsWelcome ?? fallbackRule.walkInsWelcome)
    };
  });
};

const sortDayOverrides = (overrides) => {
  return [...(overrides || [])].sort((a, b) => String(a?.dayKey || '').localeCompare(String(b?.dayKey || '')));
};

const getWeekdayInTimeZone = (date, timeZone = SHOP_TIMEZONE) => {
  if (!(date instanceof Date) || Number.isNaN(date.valueOf())) return null;
  const weekdayName = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short'
  }).format(date);

  return WEEKDAY_INDEX_BY_SHORT_NAME[weekdayName] ?? null;
};

const buildBaseTimeSlots = ({
  date,
  firstSlotTime,
  lastSlotTime,
  blockedRanges = []
}) => {
  const slots = [];
  const firstMinutes = parseTimeToMinutes(firstSlotTime);
  const lastMinutes = parseTimeToMinutes(lastSlotTime);
  if (firstMinutes == null || lastMinutes == null || lastMinutes < firstMinutes) {
    return slots;
  }

  const now = new Date();
  const todayKey = formatDateLocal(now);
  const selectedKey = formatDateLocal(date);
  const isToday = Boolean(selectedKey && todayKey && selectedKey === todayKey);
  const { hour: nowHour, minute: nowMinute } = getDateTimePartsInTimeZone(now, SHOP_TIMEZONE);
  const nowMinutes = nowHour * 60 + nowMinute;

  for (let slotMinutes = firstMinutes; slotMinutes <= lastMinutes; slotMinutes += SLOT_INTERVAL_MINUTES) {
    const time = formatMinutesToTime(slotMinutes);
    const isBlocked = blockedRanges.some((range) => slotMinutes >= range.start && slotMinutes < range.end);
    const isPast = isToday && slotMinutes < nowMinutes;
    slots.push({
      time,
      available: !isBlocked && !isPast
    });
  }

  return slots;
};

// Mock appointments data
const initialAppointments = [
  {
    id: '1',
    customerName: 'John Smith',
    customerEmail: 'john@example.com',
    customerPhone: '555-123-4567',
    service: initialServices[0],
    barber: initialBarbers[0],
    date: new Date(),
    dateKey: formatDateLocal(new Date()),
    time: '09:00',
    status: 'confirmed',
    createdAt: new Date(Date.now() - 86400000)
  },
  {
    id: '2',
    customerName: 'Mike Brown',
    customerEmail: 'mike@example.com',
    customerPhone: '555-234-5678',
    service: initialServices[2],
    barber: initialBarbers[1],
    date: new Date(),
    dateKey: formatDateLocal(new Date()),
    time: '10:30',
    status: 'confirmed',
    createdAt: new Date(Date.now() - 172800000)
  },
  {
    id: '3',
    customerName: 'Robert Davis',
    customerEmail: 'robert@example.com',
    customerPhone: '555-345-6789',
    service: initialServices[1],
    barber: initialBarbers[2],
    date: new Date(),
    dateKey: formatDateLocal(new Date()),
    time: '14:00',
    status: 'pending',
    createdAt: new Date(Date.now() - 43200000)
  }
];

// Mock blocked times
const initialBlockedTimes = [
  {
    id: '1',
    title: 'Lunch Break',
    date: new Date(),
    dayKey: formatLocalCalendarDate(new Date()),
    startTime: '12:00',
    endTime: '13:00',
    reason: 'Daily lunch break'
  }
];

const initialBookingWeeklyRules = normalizeWeeklyRules([]);
const initialBookingDayOverrides = [];

// Mock promos
const initialPromos = [
  {
    id: '1',
    code: 'WELCOME10',
    discount: 10,
    type: 'percentage',
    description: '10% off for new customers',
    validUntil: new Date(Date.now() + 30 * 86400000),
    usageLimit: 100,
    usedCount: 23,
    active: true
  },
  {
    id: '2',
    code: 'SUMMER25',
    discount: 25,
    type: 'percentage',
    description: 'Summer special discount',
    validUntil: new Date(Date.now() + 60 * 86400000),
    usageLimit: 50,
    usedCount: 12,
    active: true
  }
];

const initialPromoBanner = {
  id: 1,
  title: '',
  imageData: '',
  imageAlt: DEFAULT_PROMO_IMAGE_ALT,
  active: false,
  createdAt: new Date(),
  updatedAt: new Date()
};

export function BookingProvider({ children }) {
  const [services, setServices] = useState(() => (supabase ? [] : initialServices));
  const [barbers, setBarbers] = useState(() => {
    if (!supabase) return initialBarbers;
    const anyAvailable = initialBarbers[initialBarbers.length - 1];
    return [anyAvailable];
  });
  const [appointments, setAppointments] = useState(() => (supabase ? [] : initialAppointments));
  const [blockedTimes, setBlockedTimes] = useState(() => (supabase ? [] : initialBlockedTimes));
  const [bookingWeeklyRules, setBookingWeeklyRules] = useState(() => (supabase ? initialBookingWeeklyRules : initialBookingWeeklyRules));
  const [bookingDayOverrides, setBookingDayOverrides] = useState(() => (supabase ? [] : initialBookingDayOverrides));
  const [promos, setPromos] = useState(() => (supabase ? [] : initialPromos));
  const [promoBanner, setPromoBanner] = useState(() => (supabase ? null : initialPromoBanner));
  const [isLoading, setIsLoading] = useState(() => Boolean(supabase));
  const [loadError, setLoadError] = useState(null);
  const [shopMetrics, setShopMetrics] = useState(null);
  const [serviceMetrics, setServiceMetrics] = useState([]);
  const [dailyMetrics, setDailyMetrics] = useState([]);

  const servicesByIdRef = useRef(new Map());
  const barbersByIdRef = useRef(new Map());
  const metricsRefreshTimerRef = useRef(null);
  const backgroundRefreshInFlightRef = useRef(false);
  const backgroundRefreshTimerRef = useRef(null);
  const whatsappFunctionPausedRef = useRef(false);
  const whatsappPauseLoggedRef = useRef(false);
  
  // Booking flow state
  const [selectedService, setSelectedService] = useState(null);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    country: 'CA',
    phone: ''
  });

  const mapServiceRow = (row) => ({
    id: row.id,
    name: row.name,
    description: row.description,
    price: Number(row.price),
    duration: row.duration_minutes,
    image: row.image
  });

  const mapBarberRow = (row) => ({
    id: row.id,
    name: row.name,
    title: row.title,
    experience: row.experience,
    specialty: row.specialty,
    image: row.image,
    rating: row.rating,
    reviews: row.reviews
  });

  const mapAppointmentRow = (row) => ({
    id: row.id,
    customerName: row.customer_name,
    customerEmail: row.customer_email,
    customerPhone: row.customer_phone,
    service: row.service ? mapServiceRow(row.service) : null,
    barber: row.barber ? mapBarberRow(row.barber) : null,
    // Use midday for date-only fields to avoid timezone day shifting.
    date: row.appointment_date ? new Date(`${row.appointment_date}T12:00:00`) : null,
    dateKey: row.appointment_date || null,
    time: row.appointment_time,
    status: row.status,
    amount: row.amount != null ? Number(row.amount) : null,
    promoCode: row.promo_code ? String(row.promo_code).trim().toUpperCase() : null,
    discountAmount: row.discount_amount != null ? Number(row.discount_amount) : null,
    completedAt: row.completed_at ? new Date(row.completed_at) : null,
    createdAt: row.created_at ? new Date(row.created_at) : null
  });

  const mapAppointmentRowLite = (row) => {
    const serviceId = row?.service_id != null ? String(row.service_id) : null;
    const barberId = row?.barber_id != null ? String(row.barber_id) : null;
    return {
      id: row.id,
      customerName: row.customer_name,
      customerEmail: row.customer_email,
      customerPhone: row.customer_phone,
      service: serviceId ? servicesByIdRef.current.get(serviceId) || null : null,
      barber: barberId ? barbersByIdRef.current.get(barberId) || null : null,
      date: row.appointment_date ? new Date(`${row.appointment_date}T12:00:00`) : null,
      dateKey: row.appointment_date || null,
      time: row.appointment_time,
      status: row.status,
      amount: row.amount != null ? Number(row.amount) : null,
      promoCode: row.promo_code ? String(row.promo_code).trim().toUpperCase() : null,
      discountAmount: row.discount_amount != null ? Number(row.discount_amount) : null,
      completedAt: row.completed_at ? new Date(row.completed_at) : null,
      createdAt: row.created_at ? new Date(row.created_at) : null
    };
  };

  const mapBlockedTimeRow = (row) => ({
    id: row.id,
    title: row.title,
    date: row.day ? new Date(`${row.day}T12:00:00`) : null,
    dayKey: row.day || null,
    startTime: row.start_time,
    endTime: row.end_time,
    reason: row.reason
  });

  const mapBookingWeeklyRuleRow = (row) => ({
    weekday: Number(row.weekday),
    enabled: row.enabled !== false,
    firstSlotTime: String(row.first_slot_time || DEFAULT_BOOKING_WINDOW.firstSlotTime),
    lastSlotTime: String(row.last_slot_time || DEFAULT_BOOKING_WINDOW.lastSlotTime),
    closingTime: String(row.closing_time || row.last_slot_time || DEFAULT_BOOKING_WINDOW.closingTime),
    walkInsWelcome: Boolean(row.walk_ins_welcome),
    updatedAt: row.updated_at ? new Date(row.updated_at) : null
  });

  const mapBookingDayOverrideRow = (row) => ({
    day: row.day ? new Date(`${row.day}T12:00:00`) : null,
    dayKey: row.day || null,
    mode: row.mode || 'default',
    firstSlotTime: row.first_slot_time || null,
    lastSlotTime: row.last_slot_time || null,
    walkInsWelcome: Boolean(row.walk_ins_welcome),
    note: row.note || '',
    updatedAt: row.updated_at ? new Date(row.updated_at) : null
  });

  const mapPromoRow = (row) => ({
    id: row.id,
    code: row.code,
    discount: row.discount != null ? Number(row.discount) : 0,
    type: row.type,
    description: row.description,
    validUntil: row.valid_until ? new Date(`${row.valid_until}T12:00:00`) : null,
    usageLimit: row.usage_limit,
    usedCount: row.used_count,
    active: row.active
  });

  const mapPromoBannerRow = (row) => {
    if (!row) return null;
    return {
      id: row.id,
      title: row.title || '',
      imageData: row.image_data || '',
      imageAlt: row.image_alt || DEFAULT_PROMO_IMAGE_ALT,
      active: row.active ?? false,
      updatedAt: row.updated_at ? new Date(row.updated_at) : null,
      createdAt: row.created_at ? new Date(row.created_at) : null
    };
  };

  const isCancelledStatus = (status) => {
    const normalized = String(status || '').toLowerCase();
    return normalized === 'cancelled' || normalized === 'canceled';
  };

  const isAnyAvailableBarber = (barber) => {
    const normalizedName = String(barber?.name || '').trim().toLowerCase();
    return (
      barber?.id === ANY_AVAILABLE_BARBER_ID ||
      normalizedName === 'any available' ||
      normalizedName === 'any available chair'
    );
  };

  const getChairLabel = (barber) => {
    if (!barber) return '';
    if (isAnyAvailableBarber(barber)) return 'Next Available Chair';

    const explicitNumber = parseChairNumber(barber);
    if (explicitNumber != null) return `Chair ${explicitNumber}`;

    const knownBarbers = sortBarbersByChairNumber((barbers || []).filter((b) => b?.id && !isAnyAvailableBarber(b)));
    const merged = barber?.id && !knownBarbers.some((b) => b.id === barber.id)
      ? sortBarbersByChairNumber([...knownBarbers, barber])
      : knownBarbers;

    const index = merged.findIndex((b) => b.id === barber.id);
    if (index >= 0) return `Chair ${index + 1}`;

    return 'Chair';
  };

  const getBookedTimesByBarberForDate = (dateKey) => {
    const bookedByBarber = new Map();
    (appointments || []).forEach((appt) => {
      if (!appt?.barber?.id || !appt?.date || !appt?.time) return;
      if (isCancelledStatus(appt.status)) return;
      const apptKey = appt?.dateKey || formatDateLocal(appt.date);
      if (!apptKey || apptKey !== dateKey) return;
      const set = bookedByBarber.get(appt.barber.id) || new Set();
      set.add(appt.time);
      bookedByBarber.set(appt.barber.id, set);
    });
    return bookedByBarber;
  };

  const getBlockedRangesForDate = (dateKey) => {
    return (blockedTimes || [])
      .filter((blockedTime) => {
        const blockedKey = blockedTime?.dayKey || formatDateLocal(blockedTime?.date);
        return Boolean(dateKey && blockedKey && blockedKey === dateKey);
      })
      .map((blockedTime) => {
        const start = parseTimeToMinutes(blockedTime?.startTime);
        const end = parseTimeToMinutes(blockedTime?.endTime);
        return { start, end };
      })
      .filter((range) => range.start != null && range.end != null && range.end > range.start);
  };

  const getWeeklyRuleForDate = (date) => {
    const weekday = getWeekdayInTimeZone(date);
    if (weekday == null) return null;
    const rules = normalizeWeeklyRules(bookingWeeklyRules);
    return rules.find((rule) => rule.weekday === weekday) || null;
  };

  const getDayOverrideForDate = (dateKey) => {
    return (bookingDayOverrides || []).find((override) => override?.dayKey === dateKey) || null;
  };

  const buildCustomerAvailabilityMessage = ({
    mode,
    availableSlotsCount,
    walkInsWelcome,
    closingTime,
    note,
    hasChairConfiguration
  }) => {
    const parts = [];

    if (mode === 'fully_booked') {
      parts.push('This day is fully booked.');
    } else if (mode === 'booking_closed') {
      parts.push('Online booking is closed for this day.');
    } else if (!hasChairConfiguration) {
      parts.push('No chairs are currently configured for online booking.');
    } else if (availableSlotsCount === 0) {
      parts.push('No online booking times are currently available for this day.');
    } else {
      parts.push('Choose a time to continue.');
    }

    if (walkInsWelcome) {
      if (closingTime) {
        parts.push(`Walk-ins are welcome until ${closingTime}.`);
      } else {
        parts.push('Walk-ins are welcome.');
      }
    }

    if (note) {
      parts.push(String(note).trim());
    }

    return parts.join(' ').trim();
  };

  const getEffectiveBookingWindowForDate = (date) => {
    const dateKey = formatDateLocal(date);
    if (!dateKey) {
      return {
        dateKey: null,
        mode: 'booking_closed',
        firstSlotTime: null,
        lastSlotTime: null,
        closingTime: null,
        walkInsWelcome: false,
        note: '',
        bookingsEnabled: false
      };
    }

    const weeklyRule = getWeeklyRuleForDate(date);
    const weeklyClosingTime = weeklyRule?.closingTime || weeklyRule?.lastSlotTime || null;
    const weeklyWalkInsWelcome = Boolean(weeklyRule?.walkInsWelcome);
    const override = getDayOverrideForDate(dateKey);
    const configuredMode = override?.mode || 'default';
    const effectiveWalkInsWelcome = override ? Boolean(override?.walkInsWelcome) : weeklyWalkInsWelcome;

    if (configuredMode === 'fully_booked') {
      return {
        dateKey,
        mode: 'fully_booked',
        firstSlotTime: null,
        lastSlotTime: null,
        closingTime: weeklyClosingTime,
        walkInsWelcome: effectiveWalkInsWelcome,
        note: override?.note || '',
        bookingsEnabled: false
      };
    }

    if (configuredMode === 'booking_closed') {
      return {
        dateKey,
        mode: 'booking_closed',
        firstSlotTime: null,
        lastSlotTime: null,
        closingTime: weeklyClosingTime,
        walkInsWelcome: effectiveWalkInsWelcome,
        note: override?.note || '',
        bookingsEnabled: false
      };
    }

    if (configuredMode === 'custom_hours') {
      const firstMinutes = parseTimeToMinutes(override?.firstSlotTime);
      const lastMinutes = parseTimeToMinutes(override?.lastSlotTime);
      const bookingsEnabled = firstMinutes != null && lastMinutes != null && lastMinutes >= firstMinutes;
      return {
        dateKey,
        mode: bookingsEnabled ? 'custom_hours' : 'booking_closed',
        firstSlotTime: bookingsEnabled ? override?.firstSlotTime : null,
        lastSlotTime: bookingsEnabled ? override?.lastSlotTime : null,
        closingTime: weeklyClosingTime,
        walkInsWelcome: effectiveWalkInsWelcome,
        note: override?.note || '',
        bookingsEnabled
      };
    }

    const firstMinutes = parseTimeToMinutes(weeklyRule?.firstSlotTime);
    const lastMinutes = parseTimeToMinutes(weeklyRule?.lastSlotTime);
    const closingMinutes = parseTimeToMinutes(weeklyClosingTime);
    const bookingsEnabled = Boolean(
      weeklyRule?.enabled &&
      firstMinutes != null &&
      lastMinutes != null &&
      lastMinutes >= firstMinutes &&
      (closingMinutes == null || closingMinutes >= lastMinutes)
    );

    return {
      dateKey,
      mode: bookingsEnabled ? 'default' : 'booking_closed',
      firstSlotTime: bookingsEnabled ? weeklyRule?.firstSlotTime : null,
      lastSlotTime: bookingsEnabled ? weeklyRule?.lastSlotTime : null,
      closingTime: weeklyClosingTime,
      walkInsWelcome: effectiveWalkInsWelcome,
      note: override?.note || '',
      bookingsEnabled
    };
  };

  const getBookingAvailabilityForDate = (date, barber) => {
    const window = getEffectiveBookingWindowForDate(date);
    const dateKey = window.dateKey;
    const realBarbers = sortBarbersByChairNumber((barbers || []).filter((item) => item?.id && !isAnyAvailableBarber(item)));

    if (!window.bookingsEnabled || !dateKey) {
      return {
        ...window,
        slots: [],
        availableSlotsCount: 0,
        isFullyBooked: window.mode === 'fully_booked',
        isBookingClosed: window.mode === 'booking_closed',
        customerMessage: buildCustomerAvailabilityMessage({
          mode: window.mode,
          availableSlotsCount: 0,
          walkInsWelcome: window.walkInsWelcome,
          closingTime: window.closingTime,
          note: window.note,
          hasChairConfiguration: realBarbers.length > 0
        })
      };
    }

    if (realBarbers.length === 0) {
      return {
        ...window,
        slots: [],
        availableSlotsCount: 0,
        isFullyBooked: false,
        isBookingClosed: false,
        customerMessage: buildCustomerAvailabilityMessage({
          mode: window.mode,
          availableSlotsCount: 0,
          walkInsWelcome: window.walkInsWelcome,
          closingTime: window.closingTime,
          note: window.note,
          hasChairConfiguration: false
        })
      };
    }

    const blockedRanges = getBlockedRangesForDate(dateKey);
    const bookedByBarber = getBookedTimesByBarberForDate(dateKey);
    const baseSlots = buildBaseTimeSlots({
      date,
      firstSlotTime: window.firstSlotTime,
      lastSlotTime: window.lastSlotTime,
      blockedRanges
    });

    let slots = baseSlots;
    if (isAnyAvailableBarber(barber)) {
      slots = baseSlots.map((slot) => {
        if (!slot.available) return slot;
        const allBooked = realBarbers.every((item) => bookedByBarber.get(item.id)?.has(slot.time));
        return { ...slot, available: !allBooked };
      });
    } else if (barber?.id) {
      const bookedTimes = bookedByBarber.get(barber.id) || new Set();
      slots = baseSlots.map((slot) => (
        slot.available ? { ...slot, available: !bookedTimes.has(slot.time) } : slot
      ));
    }

    const availableSlotsCount = slots.filter((slot) => slot.available).length;

    return {
      ...window,
      slots,
      availableSlotsCount,
      isFullyBooked: window.mode === 'fully_booked',
      isBookingClosed: window.mode === 'booking_closed',
      customerMessage: buildCustomerAvailabilityMessage({
        mode: window.mode,
        availableSlotsCount,
        walkInsWelcome: window.walkInsWelcome,
        closingTime: window.closingTime,
        note: window.note,
        hasChairConfiguration: true
      })
    };
  };

  const refreshMetricsFromSupabase = async () => {
    if (!supabase) return;

    const since = new Date();
    since.setDate(since.getDate() - 6);
    const sinceIso = formatDateLocal(since);

    const [shopRes, serviceRes, dailyRes] = await Promise.all([
      supabase.from('shop_metrics').select('*').eq('id', 1).maybeSingle(),
      supabase
        .from('service_metrics')
        .select('service_id, booked_count, completed_count, revenue, service:services(*)')
        .order('booked_count', { ascending: false })
        .limit(8),
      supabase
        .from('daily_metrics')
        .select('*')
        .gte('day', sinceIso)
        .order('day', { ascending: true })
    ]);

    if (shopRes.error) throw shopRes.error;
    if (serviceRes.error) throw serviceRes.error;
    if (dailyRes.error) throw dailyRes.error;

    setShopMetrics(shopRes.data || null);
    setServiceMetrics(serviceRes.data || []);
    setDailyMetrics(dailyRes.data || []);
  };

  const refreshFromSupabase = async () => {
    if (!supabase) return;

    setIsLoading(true);
    setLoadError(null);
    try {
      const [servicesRes, barbersRes, appointmentsRes] = await Promise.all([
        supabase.from('services').select('*').order('name', { ascending: true }),
        supabase.from('barbers').select('*').order('name', { ascending: true }),
        supabase
          .from('appointments')
          .select('*, service:services(*), barber:barbers(*)')
          .order('appointment_date', { ascending: false })
          .order('appointment_time', { ascending: false })
      ]);

      if (servicesRes.error) throw servicesRes.error;
      if (barbersRes.error) throw barbersRes.error;
      if (appointmentsRes.error) throw appointmentsRes.error;

      setServices((servicesRes.data || []).map(mapServiceRow));
      const anyAvailable = initialBarbers[initialBarbers.length - 1];
      const dbBarbers = sortBarbersByChairNumber(
        (barbersRes.data || [])
          .filter((b) => !isAnyAvailableBarber(b))
          .map(mapBarberRow)
      );
      setBarbers([...dbBarbers, anyAvailable]);
      setAppointments((appointmentsRes.data || []).map(mapAppointmentRow));

      // Optional tables (admin-only features). If missing, keep UI working.
      try {
        const [blockedRes, promosRes, promoBannerRes, weeklyRulesRes, dayOverridesRes] = await Promise.all([
          supabase.from('blocked_times').select('*').order('day', { ascending: false }),
          supabase.from('promos').select('*').order('created_at', { ascending: false }),
          supabase.from('promo_banners').select('*').eq('id', 1).maybeSingle(),
          supabase.from('booking_weekly_rules').select('*').order('weekday', { ascending: true }),
          supabase.from('booking_day_overrides').select('*').order('day', { ascending: true })
        ]);

        if (!blockedRes.error) {
          const blocked = (blockedRes.data || [])
            .map(mapBlockedTimeRow)
            .filter((bt) => bt.date instanceof Date && !Number.isNaN(bt.date.valueOf()));
          setBlockedTimes(blocked);
        }

        if (!weeklyRulesRes.error) {
          setBookingWeeklyRules(normalizeWeeklyRules((weeklyRulesRes.data || []).map(mapBookingWeeklyRuleRow)));
        }

        if (!dayOverridesRes.error) {
          const overrides = sortDayOverrides((dayOverridesRes.data || []).map(mapBookingDayOverrideRow));
          setBookingDayOverrides(overrides);
        }

        if (!promosRes.error) {
          const promosData = (promosRes.data || [])
            .map(mapPromoRow)
            .filter((p) => p.validUntil instanceof Date && !Number.isNaN(p.validUntil.valueOf()));
          setPromos(promosData);
        }

        if (!promoBannerRes.error) {
          setPromoBanner(mapPromoBannerRow(promoBannerRes.data));
        }
      } catch (error) {
        // ignore missing tables/policies
      }

      // Metrics are optional; if missing tables/policies, keep the UI working.
      try {
        await refreshMetricsFromSupabase();
      } catch (error) {
        // ignore metrics errors; core data still loads
      }
    } catch (error) {
      setLoadError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshFromSupabase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAdminDataInBackground = async () => {
    if (!supabase) return;
    if (backgroundRefreshInFlightRef.current) return;

    backgroundRefreshInFlightRef.current = true;
    try {
      const [servicesRes, barbersRes, appointmentsRes, blockedRes, promosRes, promoBannerRes, weeklyRulesRes, dayOverridesRes] = await Promise.all([
        supabase.from('services').select('*').order('name', { ascending: true }),
        supabase.from('barbers').select('*').order('name', { ascending: true }),
        supabase
          .from('appointments')
          .select('*, service:services(*), barber:barbers(*)')
          .order('appointment_date', { ascending: false })
          .order('appointment_time', { ascending: false }),
        supabase.from('blocked_times').select('*').order('day', { ascending: false }),
        supabase.from('promos').select('*').order('created_at', { ascending: false }),
        supabase.from('promo_banners').select('*').eq('id', 1).maybeSingle(),
        supabase.from('booking_weekly_rules').select('*').order('weekday', { ascending: true }),
        supabase.from('booking_day_overrides').select('*').order('day', { ascending: true })
      ]);

      if (!servicesRes.error) {
        setServices((servicesRes.data || []).map(mapServiceRow));
      }

      if (!barbersRes.error) {
        const anyAvailable = initialBarbers[initialBarbers.length - 1];
        const dbBarbers = sortBarbersByChairNumber(
          (barbersRes.data || [])
            .filter((b) => !isAnyAvailableBarber(b))
            .map(mapBarberRow)
        );
        setBarbers([...dbBarbers, anyAvailable]);
      }

      if (!appointmentsRes.error) {
        setAppointments((appointmentsRes.data || []).map(mapAppointmentRow));
      }

      if (!blockedRes.error) {
        const blocked = (blockedRes.data || [])
          .map(mapBlockedTimeRow)
          .filter((bt) => bt.date instanceof Date && !Number.isNaN(bt.date.valueOf()));
        setBlockedTimes(blocked);
      }

      if (!weeklyRulesRes.error) {
        setBookingWeeklyRules(normalizeWeeklyRules((weeklyRulesRes.data || []).map(mapBookingWeeklyRuleRow)));
      }

      if (!dayOverridesRes.error) {
        setBookingDayOverrides(sortDayOverrides((dayOverridesRes.data || []).map(mapBookingDayOverrideRow)));
      }

      if (!promosRes.error) {
        const promosData = (promosRes.data || [])
          .map(mapPromoRow)
          .filter((p) => p.validUntil instanceof Date && !Number.isNaN(p.validUntil.valueOf()));
        setPromos(promosData);
      }

      if (!promoBannerRes.error) {
        setPromoBanner(mapPromoBannerRow(promoBannerRes.data));
      }

      // Metrics are optional; refresh silently.
      try {
        await refreshMetricsFromSupabase();
      } catch (e) {
        // ignore metrics errors
      }
    } finally {
      backgroundRefreshInFlightRef.current = false;
    }
  };

  const scheduleBackgroundRefresh = (delayMs = BACKGROUND_SYNC_DEBOUNCE_MS) => {
    if (!supabase) return;
    if (backgroundRefreshTimerRef.current) {
      clearTimeout(backgroundRefreshTimerRef.current);
    }

    backgroundRefreshTimerRef.current = setTimeout(() => {
      backgroundRefreshTimerRef.current = null;
      refreshAdminDataInBackground();
    }, delayMs);
  };

  useEffect(() => {
    const map = new Map();
    (services || []).forEach((s) => {
      if (s?.id == null) return;
      map.set(String(s.id), s);
    });
    servicesByIdRef.current = map;
  }, [services]);

  useEffect(() => {
    const map = new Map();
    (barbers || []).forEach((b) => {
      if (b?.id == null) return;
      map.set(String(b.id), b);
    });
    barbersByIdRef.current = map;
  }, [barbers]);

  useEffect(() => {
    if (!supabase) return;

    const scheduleMetricsRefresh = () => {
      if (metricsRefreshTimerRef.current) {
        clearTimeout(metricsRefreshTimerRef.current);
      }
      metricsRefreshTimerRef.current = setTimeout(async () => {
        try {
          await refreshMetricsFromSupabase();
        } catch (e) {
          // ignore metrics errors; core data still updates live
        }
      }, 400);
    };

    const channel = supabase
      .channel('realtime:global')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'services' },
        (payload) => {
          const eventType = payload.eventType;
          if (eventType === 'INSERT' && payload.new) {
            setServices((prev) => [...prev, mapServiceRow(payload.new)].sort((a, b) => a.name.localeCompare(b.name)));
          } else if (eventType === 'UPDATE' && payload.new) {
            setServices((prev) => prev.map((s) => (s.id === payload.new.id ? mapServiceRow(payload.new) : s)));
          } else if (eventType === 'DELETE' && payload.old) {
            setServices((prev) => prev.filter((s) => s.id !== payload.old.id));
          }
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'barbers' },
        () => {
          // Barbers/chairs affect labels and availability across pages.
          scheduleBackgroundRefresh();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'blocked_times' },
        () => {
          scheduleBackgroundRefresh();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'booking_weekly_rules' },
        () => {
          scheduleBackgroundRefresh();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'booking_day_overrides' },
        () => {
          scheduleBackgroundRefresh();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'promos' },
        () => {
          scheduleBackgroundRefresh();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'promo_banners' },
        () => {
          scheduleBackgroundRefresh();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'appointments' },
        async (payload) => {
          const eventType = payload?.eventType;
          if (eventType === 'INSERT' && payload?.new?.id != null) {
            const id = payload.new.id;
            const lite = mapAppointmentRowLite(payload.new);
            setAppointments((prev) => {
              if (prev.some((a) => a?.id === id)) return prev;
              return [lite, ...prev];
            });

            // Fetch the full row w/ joined barber + service so UI has names/images.
            try {
              const { data, error } = await supabase
                .from('appointments')
                .select('*, service:services(*), barber:barbers(*)')
                .eq('id', id)
                .single();
              if (!error && data) {
                const full = mapAppointmentRow(data);
                setAppointments((prev) => prev.map((a) => (a?.id === id ? full : a)));
              }
            } catch (e) {
              // ignore; lite row still shows up
            }

            scheduleMetricsRefresh();
            return;
          }

          if (eventType === 'UPDATE' && payload?.new?.id != null) {
            const id = payload.new.id;
            const patch = mapAppointmentRowLite(payload.new);
            setAppointments((prev) => prev.map((a) => (a?.id === id ? { ...a, ...patch } : a)));
            scheduleMetricsRefresh();
            return;
          }

          if (eventType === 'DELETE' && payload?.old?.id != null) {
            const id = payload.old.id;
            setAppointments((prev) => prev.filter((a) => a?.id !== id));
            scheduleMetricsRefresh();
          }
        }
      )
      .subscribe();

    return () => {
      if (metricsRefreshTimerRef.current) {
        clearTimeout(metricsRefreshTimerRef.current);
      }
      if (backgroundRefreshTimerRef.current) {
        clearTimeout(backgroundRefreshTimerRef.current);
        backgroundRefreshTimerRef.current = null;
      }
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!supabase) return;

    const refreshAdminContinuously = () => {
      refreshAdminDataInBackground();
    };

    const refreshMetricsContinuously = async () => {
      try {
        await refreshMetricsFromSupabase();
      } catch (e) {
        // ignore metrics errors; keep polling in background
      }
    };

    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        scheduleBackgroundRefresh(120);
      }
    };

    const adminInterval = setInterval(refreshAdminContinuously, BACKGROUND_SYNC_INTERVAL_MS);
    const metricsInterval = setInterval(refreshMetricsContinuously, METRICS_SYNC_INTERVAL_MS);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      clearInterval(adminInterval);
      clearInterval(metricsInterval);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get available time slots for a date (and optional barber)
  const getTimeSlots = (date, barber) => {
    return getBookingAvailabilityForDate(date, barber).slots;
  };

  // Book appointment
  const bookAppointment = async (appointmentData) => {
    const trySendWhatsApp = async ({
      appointmentDateKey,
      appointmentTime,
      serviceName,
      servicePrice,
      chairLabel,
      promoCode,
      discountAmount
    }) => {
      if (!supabase || !WHATSAPP_CONFIRMATION_ENABLED) return;
      if (whatsappFunctionPausedRef.current) return;

      const phoneDigits = sanitizePhoneDigits(appointmentData?.customerPhone);
      if (!phoneDigits) return;

      try {
        const { data, error } = await supabase.functions.invoke('send-booking-confirmation', {
          body: {
            phone: phoneDigits,
            customer_name: appointmentData?.customerName || null,
            service_name: serviceName || null,
            service_price: servicePrice ?? null,
            barber_name: chairLabel || null,
            appointment_date: appointmentDateKey || null,
            appointment_time: appointmentTime || null,
            promo_code: promoCode || null,
            discount_amount: discountAmount ?? null
          }
        });

        if (error) {
          const rawStatus = error?.context?.status;
          const status = Number(rawStatus);
          const hasStatus = Number.isFinite(status);
          const shouldPause = !hasStatus || status >= 500;
          if (shouldPause) {
            whatsappFunctionPausedRef.current = true;
          }

          if (process.env.NODE_ENV !== 'production') {
            if (shouldPause && !whatsappPauseLoggedRef.current) {
              // eslint-disable-next-line no-console
              console.warn(
                `[WhatsApp] send-booking-confirmation unavailable (${hasStatus ? `HTTP ${status}` : 'network error'}). ` +
                'Pausing WhatsApp confirmations for this session. Check Supabase function secrets: ' +
                'WHATSAPP_ENABLED, COREVERDE_DEVICE_ID, COREVERDE_SUBSCRIPTION_TOKEN, STORE_WHATSAPP_TO.'
              );
              whatsappPauseLoggedRef.current = true;
            } else if (!shouldPause) {
              // eslint-disable-next-line no-console
              console.warn('[WhatsApp] Edge Function error:', error);
              // eslint-disable-next-line no-console
              console.warn('[WhatsApp] Edge Function context:', error?.context);
            }
          }
          return;
        }

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.info('[WhatsApp] Edge Function success:', data);
        }
      } catch (e) {
        // Non-blocking; booking should still succeed even if WhatsApp fails.
        whatsappFunctionPausedRef.current = true;
        if (process.env.NODE_ENV !== 'production') {
          if (!whatsappPauseLoggedRef.current) {
            // eslint-disable-next-line no-console
            console.warn(
              '[WhatsApp] send-booking-confirmation request failed. ' +
              'Pausing WhatsApp confirmations for this session.'
            );
            whatsappPauseLoggedRef.current = true;
          }
        }
      }
    };

    const promoCode = normalizePromoCode(appointmentData?.promoCode);
    const servicePrice = toMoney(appointmentData?.service?.price);
    let discountAmount = 0;
    let finalAmount = servicePrice;
    const dateKey = formatDateLocal(appointmentData?.date);
    const time = appointmentData?.time;

    if (!dateKey) throw new Error('Invalid appointment date');
    if (!time) throw new Error('Invalid appointment time');

    const selectedAvailability = getBookingAvailabilityForDate(appointmentData?.date, appointmentData?.barber);
    const selectedSlot = selectedAvailability.slots.find((slot) => slot.time === time);

    if (selectedAvailability.mode === 'fully_booked') {
      throw new Error('This day is fully booked. Please choose another date.');
    }

    if (selectedAvailability.mode === 'booking_closed') {
      throw new Error('Online booking is closed for this day. Please choose another date.');
    }

    if (!selectedSlot || !selectedSlot.available) {
      throw new Error('That time is not available. Please choose another time.');
    }

    if (!supabase) {
      if (promoCode) {
        const promo = (promos || []).find((p) => normalizePromoCode(p?.code) === promoCode);
        if (!promo) throw new Error('Promo code is invalid.');
        if (!promo.active) throw new Error('Promo code is not active.');

        const todayKey = formatDateLocal(new Date());
        const validUntilKey = promo?.validUntil ? formatDateLocal(promo.validUntil) : null;
        if (validUntilKey && todayKey && validUntilKey < todayKey) {
          throw new Error('Promo code has expired.');
        }

        const usageLimit = promo?.usageLimit != null ? Number(promo.usageLimit) : null;
        const usedCount = Number(promo?.usedCount || 0);
        if (Number.isFinite(usageLimit) && usageLimit >= 0 && usedCount >= usageLimit) {
          throw new Error('Promo code usage limit reached.');
        }

        discountAmount = computePromoDiscountAmount(promo?.type, promo?.discount, servicePrice);
        finalAmount = toMoney(servicePrice - discountAmount);

        setPromos((prev) => prev.map((item) => {
          if (normalizePromoCode(item?.code) !== promoCode) return item;
          return {
            ...item,
            usedCount: Number(item?.usedCount || 0) + 1
          };
        }));
      }

      const newAppointment = {
        id: Date.now().toString(),
        ...appointmentData,
        dateKey,
        amount: finalAmount,
        promoCode: promoCode || null,
        discountAmount: discountAmount > 0 ? discountAmount : null,
        status: 'confirmed',
        createdAt: new Date()
      };
      setAppointments(prev => [...prev, newAppointment]);

      return newAppointment;
    }

    const looksLikeUuid = (value) => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

    const serviceId = appointmentData.service?.id;
    const isAnyAvailable = isAnyAvailableBarber(appointmentData.barber);

    const bookedByBarber = getBookedTimesByBarberForDate(dateKey);

    const realBarbers = sortBarbersByChairNumber((barbers || []).filter((b) => b?.id && !isAnyAvailableBarber(b)));

    let barberId;
    if (isAnyAvailable) {
      barberId = realBarbers.find((b) => {
        const availability = getBookingAvailabilityForDate(appointmentData.date, b);
        return availability.slots.some((slot) => slot.time === time && slot.available);
      })?.id;
      if (!barberId) {
        throw new Error('No chairs available at that time. Please choose another time.');
      }
    } else {
      barberId = appointmentData.barber?.id;
    }

    if (!looksLikeUuid(serviceId) || !looksLikeUuid(barberId)) {
      throw new Error('Supabase data not loaded or missing IDs. Run the Supabase schema/seed and refresh the page.');
    }

    if (bookedByBarber.get(barberId)?.has(time)) {
      throw new Error('That time is already booked for this chair. Please choose another time.');
    }

    let reservedPromoUsage = null;
    if (promoCode) {
      const { data: promoRow, error: promoError } = await supabase
        .from('promos')
        .select('*')
        .eq('code', promoCode)
        .maybeSingle();

      if (promoError) throw promoError;
      if (!promoRow) throw new Error('Promo code is invalid.');
      if (!promoRow.active) throw new Error('Promo code is not active.');

      const todayKey = formatDateLocal(new Date());
      const validUntilKey = promoRow?.valid_until ? String(promoRow.valid_until) : null;
      if (validUntilKey && todayKey && validUntilKey < todayKey) {
        throw new Error('Promo code has expired.');
      }

      const usageLimit = promoRow?.usage_limit != null ? Number(promoRow.usage_limit) : null;
      const usedCount = Number(promoRow?.used_count || 0);
      if (Number.isFinite(usageLimit) && usageLimit >= 0 && usedCount >= usageLimit) {
        throw new Error('Promo code usage limit reached.');
      }

      const nextUsedCount = usedCount + 1;
      const { data: reservedRows, error: reserveError } = await supabase
        .from('promos')
        .update({ used_count: nextUsedCount })
        .eq('id', promoRow.id)
        .eq('used_count', usedCount)
        .eq('active', true)
        .select('id, used_count')
        .limit(1);

      if (reserveError) throw reserveError;
      if (!Array.isArray(reservedRows) || reservedRows.length === 0) {
        throw new Error('Promo code is no longer available. Please try another code.');
      }

      reservedPromoUsage = {
        id: promoRow.id,
        previousUsedCount: usedCount,
        currentUsedCount: nextUsedCount
      };

      discountAmount = computePromoDiscountAmount(promoRow?.type, promoRow?.discount, servicePrice);
      finalAmount = toMoney(servicePrice - discountAmount);
    }

    const payload = {
      customer_name: appointmentData.customerName,
      customer_email: appointmentData.customerEmail,
      customer_phone: appointmentData.customerPhone,
      service_id: serviceId,
      barber_id: barberId,
      appointment_date: dateKey,
      appointment_time: time,
      amount: finalAmount,
      promo_code: promoCode || null,
      discount_amount: discountAmount > 0 ? discountAmount : null,
      status: 'confirmed'
    };

    let data;
    try {
      const { data: inserted, error } = await supabase
        .from('appointments')
        .insert(payload)
        .select('*, service:services(*), barber:barbers(*)')
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('That time was just booked by someone else. Please pick another time.');
        }
        throw error;
      }

      data = inserted;
    } catch (error) {
      if (reservedPromoUsage) {
        try {
          await supabase
            .from('promos')
            .update({ used_count: reservedPromoUsage.previousUsedCount })
            .eq('id', reservedPromoUsage.id)
            .eq('used_count', reservedPromoUsage.currentUsedCount);
        } catch (rollbackError) {
          // ignore rollback failure; booking error still takes precedence
        }
      }
      throw error;
    }

    const newAppointment = mapAppointmentRow(data);
    setAppointments(prev => [newAppointment, ...prev]);
    scheduleBackgroundRefresh();

    // Fire-and-forget WhatsApp confirmation (do not block booking success).
    trySendWhatsApp({
      appointmentDateKey: dateKey,
      appointmentTime: time,
      serviceName: appointmentData?.service?.name,
      servicePrice: finalAmount,
      promoCode: promoCode || null,
      discountAmount: discountAmount > 0 ? discountAmount : null,
      chairLabel: getChairLabel(appointmentData?.barber)
    });

    return newAppointment;
  };

  // Reset booking flow
  const resetBooking = () => {
    setSelectedService(null);
    setSelectedBarber(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setCustomerInfo({ name: '', email: '', country: 'CA', phone: '' });
  };

  // Admin: Add/Edit/Delete services
  const addService = async (service) => {
    if (!supabase) {
      const newService = { ...service, id: Date.now().toString() };
      setServices(prev => [...prev, newService]);
      return newService;
    }

    const payload = {
      name: service.name,
      description: service.description,
      price: service.price,
      duration_minutes: service.duration,
      image: service.image
    };

    const { data, error } = await supabase.from('services').insert(payload).select('*').single();
    if (error) throw error;
    const created = mapServiceRow(data);
    setServices(prev => [...prev, created]);
    scheduleBackgroundRefresh();
    return created;
  };

  const updateService = async (id, updates) => {
    setServices(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
    if (!supabase) return;

    const payload = {
      ...(updates.name !== undefined ? { name: updates.name } : {}),
      ...(updates.description !== undefined ? { description: updates.description } : {}),
      ...(updates.price !== undefined ? { price: updates.price } : {}),
      ...(updates.duration !== undefined ? { duration_minutes: updates.duration } : {}),
      ...(updates.image !== undefined ? { image: updates.image } : {})
    };

    const { error } = await supabase.from('services').update(payload).eq('id', id);
    if (error) throw error;
    scheduleBackgroundRefresh();
  };

  const deleteService = async (id) => {
    if (!supabase) {
      setServices(prev => prev.filter(s => s.id !== id));
      return;
    }

    const { error } = await supabase.from('services').delete().eq('id', id);
    if (error) throw error;
    setServices(prev => prev.filter(s => s.id !== id));
    scheduleBackgroundRefresh();
  };

  // Admin: Manage barbers
  const addBarber = async (barber) => {
    if (!supabase) {
      const newBarber = { ...barber, id: Date.now().toString() };
      setBarbers(prev => [...prev.slice(0, -1), newBarber, prev[prev.length - 1]]);
      return newBarber;
    }

    const payload = {
      name: barber.name,
      title: barber.title,
      experience: barber.experience,
      specialty: barber.specialty,
      image: barber.image,
      rating: barber.rating,
      reviews: barber.reviews
    };

    const { data, error } = await supabase.from('barbers').insert(payload).select('*').single();
    if (error) throw error;
    const created = mapBarberRow(data);
    setBarbers(prev => [...prev.slice(0, -1), created, prev[prev.length - 1]]);
    scheduleBackgroundRefresh();
    return created;
  };

  const updateBarber = async (id, updates) => {
    setBarbers(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
    if (!supabase) return;

    const payload = {
      ...(updates.name !== undefined ? { name: updates.name } : {}),
      ...(updates.title !== undefined ? { title: updates.title } : {}),
      ...(updates.experience !== undefined ? { experience: updates.experience } : {}),
      ...(updates.specialty !== undefined ? { specialty: updates.specialty } : {}),
      ...(updates.image !== undefined ? { image: updates.image } : {}),
      ...(updates.rating !== undefined ? { rating: updates.rating } : {}),
      ...(updates.reviews !== undefined ? { reviews: updates.reviews } : {})
    };

    const { error } = await supabase.from('barbers').update(payload).eq('id', id);
    if (error) throw error;
    scheduleBackgroundRefresh();
  };

  const deleteBarber = async (id) => {
    if (!supabase) {
      setBarbers(prev => prev.filter(b => b.id !== id));
      return;
    }

    const { error } = await supabase.from('barbers').delete().eq('id', id);
    if (error) throw error;
    setBarbers(prev => prev.filter(b => b.id !== id));
    scheduleBackgroundRefresh();
  };

  // Admin: Manage appointments
  const updateAppointmentStatus = async (id, status) => {
    const current = appointments.find((a) => a.id === id);
    if (current?.status === status) return;

    if (!supabase) {
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, status } : a));
      return;
    }

    const { error } = await supabase
      .from('appointments')
      .update({ status })
      .eq('id', id);

    if (error) throw error;

    setAppointments(prev => prev.map(a => a.id === id ? { ...a, status } : a));
    scheduleBackgroundRefresh();

    // Metrics change when completing/cancelling; refresh so dashboard updates immediately.
    if (status === 'completed' || status === 'cancelled') {
      try {
        await refreshMetricsFromSupabase();
      } catch (error) {
        // non-fatal
      }
    }
  };

  const cancelAppointment = (id) => updateAppointmentStatus(id, 'cancelled');

  const completeAppointment = (id) => updateAppointmentStatus(id, 'completed');

  const saveBookingWeeklyRules = async (rules) => {
    const normalizedRules = normalizeWeeklyRules(rules);

    if (!supabase) {
      setBookingWeeklyRules(normalizedRules);
      return normalizedRules;
    }

    const payload = normalizedRules.map((rule) => ({
      weekday: rule.weekday,
      enabled: rule.enabled !== false,
      first_slot_time: rule.firstSlotTime || null,
      last_slot_time: rule.lastSlotTime || null,
      closing_time: rule.closingTime || rule.lastSlotTime || null,
      walk_ins_welcome: Boolean(rule.walkInsWelcome)
    }));

    const { data, error } = await supabase
      .from('booking_weekly_rules')
      .upsert(payload, { onConflict: 'weekday' })
      .select('*');

    if (error) throw error;

    const mapped = normalizeWeeklyRules((data || []).map(mapBookingWeeklyRuleRow));
    setBookingWeeklyRules(mapped);
    scheduleBackgroundRefresh();
    return mapped;
  };

  const saveBookingDayOverride = async (override) => {
    const dayKey = formatLocalCalendarDate(override?.day);
    if (!dayKey) throw new Error('Choose a valid date.');

    const payload = {
      day: dayKey,
      mode: override?.mode || 'default',
      first_slot_time: override?.mode === 'custom_hours' ? override?.firstSlotTime || null : null,
      last_slot_time: override?.mode === 'custom_hours' ? override?.lastSlotTime || null : null,
      walk_ins_welcome: Boolean(override?.walkInsWelcome),
      note: String(override?.note || '').trim() || null
    };

    if (!supabase) {
      const nextOverride = mapBookingDayOverrideRow(payload);
      setBookingDayOverrides((prev) => {
        const withoutCurrent = prev.filter((item) => item?.dayKey !== dayKey);
        return sortDayOverrides([...withoutCurrent, nextOverride]);
      });
      return nextOverride;
    }

    const { data, error } = await supabase
      .from('booking_day_overrides')
      .upsert(payload, { onConflict: 'day' })
      .select('*')
      .single();

    if (error) throw error;

    const mapped = mapBookingDayOverrideRow(data);
    setBookingDayOverrides((prev) => {
      const withoutCurrent = prev.filter((item) => item?.dayKey !== dayKey);
      return sortDayOverrides([...withoutCurrent, mapped]);
    });
    scheduleBackgroundRefresh();
    return mapped;
  };

  const removeBookingDayOverride = async (day) => {
    const dayKey = formatLocalCalendarDate(day);
    if (!dayKey) return;

    if (!supabase) {
      setBookingDayOverrides((prev) => prev.filter((item) => item?.dayKey !== dayKey));
      return;
    }

    const { error } = await supabase
      .from('booking_day_overrides')
      .delete()
      .eq('day', dayKey);

    if (error) throw error;

    setBookingDayOverrides((prev) => prev.filter((item) => item?.dayKey !== dayKey));
    scheduleBackgroundRefresh();
  };

  // Admin: Block time slots
  const addBlockedTime = async (blockedTime) => {
    if (!supabase) {
      const newBlockedTime = {
        ...blockedTime,
        id: Date.now().toString(),
        dayKey: formatLocalCalendarDate(blockedTime?.date)
      };
      setBlockedTimes(prev => [...prev, newBlockedTime]);
      return newBlockedTime;
    }

    const payload = {
      title: blockedTime.title,
      day: formatLocalCalendarDate(blockedTime.date),
      start_time: blockedTime.startTime,
      end_time: blockedTime.endTime,
      reason: blockedTime.reason
    };

    const { data, error } = await supabase
      .from('blocked_times')
      .insert(payload)
      .select('*')
      .single();

    if (error) throw error;
    const created = mapBlockedTimeRow(data);
    setBlockedTimes(prev => [...prev, created]);
    scheduleBackgroundRefresh();
    return created;
  };

  const removeBlockedTime = async (id) => {
    if (!supabase) {
      setBlockedTimes(prev => prev.filter(bt => bt.id !== id));
      return;
    }

    const { error } = await supabase.from('blocked_times').delete().eq('id', id);
    if (error) throw error;
    setBlockedTimes(prev => prev.filter(bt => bt.id !== id));
    scheduleBackgroundRefresh();
  };

  // Admin: Manage promos
  const addPromo = async (promo) => {
    if (!supabase) {
      const newPromo = { ...promo, id: Date.now().toString(), usedCount: 0 };
      setPromos(prev => [...prev, newPromo]);
      return newPromo;
    }

    const payload = {
      code: promo.code,
      discount: promo.discount,
      type: promo.type,
      description: promo.description,
      valid_until: formatDateLocal(promo.validUntil),
      usage_limit: promo.usageLimit,
      used_count: promo.usedCount ?? 0,
      active: promo.active ?? true
    };

    const { data, error } = await supabase.from('promos').insert(payload).select('*').single();
    if (error) throw error;
    const created = mapPromoRow(data);
    setPromos(prev => [created, ...prev]);
    scheduleBackgroundRefresh();
    return created;
  };

  const updatePromo = async (id, updates) => {
    setPromos(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
    if (!supabase) return;

    const payload = {
      ...(updates.code !== undefined ? { code: updates.code } : {}),
      ...(updates.discount !== undefined ? { discount: updates.discount } : {}),
      ...(updates.type !== undefined ? { type: updates.type } : {}),
      ...(updates.description !== undefined ? { description: updates.description } : {}),
      ...(updates.validUntil !== undefined ? { valid_until: formatDateLocal(updates.validUntil) } : {}),
      ...(updates.usageLimit !== undefined ? { usage_limit: updates.usageLimit } : {}),
      ...(updates.usedCount !== undefined ? { used_count: updates.usedCount } : {}),
      ...(updates.active !== undefined ? { active: updates.active } : {})
    };

    const { error } = await supabase.from('promos').update(payload).eq('id', id);
    if (error) throw error;
    scheduleBackgroundRefresh();
  };

  const deletePromo = async (id) => {
    if (!supabase) {
      setPromos(prev => prev.filter(p => p.id !== id));
      return;
    }

    const { error } = await supabase.from('promos').delete().eq('id', id);
    if (error) throw error;
    setPromos(prev => prev.filter(p => p.id !== id));
    scheduleBackgroundRefresh();
  };

  const savePromoBanner = async (bannerInput) => {
    const normalizedTitle = String(bannerInput?.title || '').trim();
    const normalizedImageData = String(bannerInput?.imageData || '');
    const normalizedImageAlt = String(
      bannerInput?.imageAlt || normalizedTitle || DEFAULT_PROMO_IMAGE_ALT
    ).trim() || DEFAULT_PROMO_IMAGE_ALT;
    const normalizedActive = Boolean(bannerInput?.active) && Boolean(normalizedImageData);

    if (!supabase) {
      const now = new Date();
      const localBanner = {
        id: 1,
        title: normalizedTitle,
        imageData: normalizedImageData,
        imageAlt: normalizedImageAlt,
        active: normalizedActive,
        createdAt: promoBanner?.createdAt || now,
        updatedAt: now
      };
      setPromoBanner(localBanner);
      return localBanner;
    }

    const payload = {
      id: 1,
      title: normalizedTitle || null,
      image_data: normalizedImageData || null,
      image_alt: normalizedImageAlt,
      active: normalizedActive,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('promo_banners')
      .upsert(payload, { onConflict: 'id' })
      .select('*')
      .single();

    if (error) throw error;

    const mapped = mapPromoBannerRow(data);
    setPromoBanner(mapped);
    scheduleBackgroundRefresh();
    return mapped;
  };

  const clearPromoBanner = async () => {
    if (!supabase) {
      const now = new Date();
      const localBanner = {
        ...initialPromoBanner,
        id: 1,
        createdAt: promoBanner?.createdAt || now,
        updatedAt: now
      };
      setPromoBanner(localBanner);
      return localBanner;
    }

    const payload = {
      id: 1,
      title: null,
      image_data: null,
      image_alt: DEFAULT_PROMO_IMAGE_ALT,
      active: false,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('promo_banners')
      .upsert(payload, { onConflict: 'id' })
      .select('*')
      .single();

    if (error) throw error;

    const mapped = mapPromoBannerRow(data);
    setPromoBanner(mapped);
    scheduleBackgroundRefresh();
    return mapped;
  };

  // Stats
  const getStats = () => {
    const today = new Date();
    const datedAppointments = appointments.filter(a => a.date instanceof Date && !Number.isNaN(a.date.valueOf()));
    const todayAppointments = datedAppointments.filter(a => a.date.toDateString() === today.toDateString());
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());
    const weekAppointments = datedAppointments.filter(a => a.date >= weekStart && a.date <= today);
    
    return {
      todayCount: todayAppointments.length,
      weekCount: weekAppointments.length,
      allTimeCount: datedAppointments.length,
      todayRevenue: todayAppointments.reduce((sum, a) => sum + Number(a.amount ?? a.service?.price ?? 0), 0),
      weekRevenue: weekAppointments.reduce((sum, a) => sum + Number(a.amount ?? a.service?.price ?? 0), 0),
      allTimeRevenue: datedAppointments.reduce((sum, a) => sum + Number(a.amount ?? a.service?.price ?? 0), 0),
      pendingCount: appointments.filter(a => a.status === 'pending').length,
      completedCount: appointments.filter(a => a.status === 'completed').length
    };
  };

  const value = {
      isLoading,
      loadError,
      refreshFromSupabase,
      refreshAdminDataInBackground,
      refreshMetricsFromSupabase,
      shopMetrics,
      serviceMetrics,
      dailyMetrics,
    // Data
    services,
    barbers,
    appointments,
    blockedTimes,
    bookingWeeklyRules,
    bookingDayOverrides,
    promos,
    promoBanner,
    
    // Booking flow state
    selectedService,
    selectedBarber,
    selectedDate,
    selectedTime,
    customerInfo,
    
    // Booking flow actions
    setSelectedService,
    setSelectedBarber,
    setSelectedDate,
    setSelectedTime,
    setCustomerInfo,
    getBookingAvailabilityForDate,
    getTimeSlots,
    getChairLabel,
    bookAppointment,
    resetBooking,
    
    // Admin service actions
    addService,
    updateService,
    deleteService,
    
    // Admin barber actions
    addBarber,
    updateBarber,
    deleteBarber,
    
    // Admin appointment actions
    updateAppointmentStatus,
    cancelAppointment,
    completeAppointment,
    
    // Admin blocked time actions
    saveBookingWeeklyRules,
    saveBookingDayOverride,
    removeBookingDayOverride,
    addBlockedTime,
    removeBlockedTime,
    
    // Admin promo actions
    addPromo,
    updatePromo,
    deletePromo,
    savePromoBanner,
    clearPromoBanner,
    
    // Stats
    getStats
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
}
